import React, { useState } from 'react';
import { PiGlobeSimpleLight, PiInfoLight, PiLinkLight } from 'react-icons/pi';
import { WebsiteStatus, type WebsiteReadResponse } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { SectionHeading } from 'src/modules/shared/components/molecules/section-heading/section-heading';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getCustomDomainErrorKey } from '../../utils/website-domain.util';
import { buildProvisionalSiteUrl } from '../../utils/website-site-url.util';
import { WEBSITE_DOMAIN_INSTRUCTION_KEYS } from '../../utils/website-status-labels.util';
import { WebsiteDomainCard } from '../molecules/website-domain-card';
import { WebsiteStatusSummary } from '../molecules/website-status-summary';

/** Props for the web address step. */
interface WebsiteAddressStepProps {
  /** Current website, when one exists. */
  website?: WebsiteReadResponse | null;
  /** Provisional slug in the draft. */
  provisionalSlug: string;
  /** Error shown when the slug is taken. */
  provisionalSlugError: string | null;
  /** Public URL of the live site, when it is published. */
  liveSiteUrl: string | null;
  /** Whether the current user may not edit the website. */
  disabled: boolean;
  /** Custom domain being typed. */
  customDomain: string;
  /** Whether a custom domain is being added. */
  creatingDomain: boolean;
  /** Whether a custom domain is being checked. */
  verifyingDomain: boolean;
  /** Called when the slug changes. */
  onProvisionalSlugChange: (value: string) => void;
  /** Called when the custom domain input changes. */
  onCustomDomainChange: (value: string) => void;
  /** Adds the typed custom domain. */
  onCreateDomain: () => void;
  /** Checks the DNS state of a custom domain. */
  onVerifyDomain: (domainId: number) => void;
}

/**
 * Web address step: the provisional address, custom domains with setup
 * instructions and their DNS records, and the publication status. Domains can
 * only be added to a published site, as the backend requires.
 *
 * @param props - Website, address state, and callbacks.
 * @returns The address step panel.
 */
export const WebsiteAddressStep: React.FC<WebsiteAddressStepProps> = ({
  website,
  provisionalSlug,
  provisionalSlugError,
  liveSiteUrl,
  disabled,
  customDomain,
  creatingDomain,
  verifyingDomain,
  onProvisionalSlugChange,
  onCustomDomainChange,
  onCreateDomain,
  onVerifyDomain,
}) => {
  const { t } = useI18n();
  const provisionalUrl = provisionalSlug
    ? buildProvisionalSiteUrl(provisionalSlug) ??
      t('websites.settings.address.provisionalNotConfigured')
    : t('websites.settings.status.pending');
  const statusProvisionalUrl =
    website?.siteUrl && !website.primaryDomain
      ? website.siteUrl
      : provisionalUrl;
  const canAddDomain = website?.status === WebsiteStatus.PUBLISHED;
  const customDomainErrorKey = getCustomDomainErrorKey(customDomain);
  const [showDomainError, setShowDomainError] = useState(false);
  const domainError =
    showDomainError && customDomainErrorKey ? t(customDomainErrorKey) : null;

  const addDomain = () => {
    if (customDomainErrorKey) {
      setShowDomainError(true);
      return;
    }
    onCreateDomain();
  };

  return (
    <section className="space-y-4">
      <SectionHeading
        title={t('websites.settings.address.title')}
        description={t('websites.settings.address.description')}
      />
      <InlineFormSection
        title={t('websites.settings.address.provisional.title')}
        icon={<PiLinkLight className="h-5 w-5" aria-hidden />}
      >
        <LabelTextInput
          label={t('websites.settings.provisionalSlug.label')}
          labelPosition="top"
          inputProps={{
            'aria-label': t('websites.settings.provisionalSlug.label'),
            value: provisionalSlug,
            error: provisionalSlugError ?? undefined,
            disabled,
            onChange: (event) => onProvisionalSlugChange(event.target.value),
          }}
        />
        <Text size="small" color="secondary">
          {t('websites.settings.address.provisionalUrl', {
            url: provisionalUrl,
          })}
        </Text>
      </InlineFormSection>

      <InlineFormSection
        title={t('websites.settings.domains.title')}
        icon={<PiGlobeSimpleLight className="h-5 w-5" aria-hidden />}
      >
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <Text weight="medium">
            {t('websites.settings.domains.instructions.title')}
          </Text>
          <Text size="small" color="secondary" className="mt-1" disableAutoLink>
            {t('websites.settings.domains.instructions.description')}
          </Text>
          <ol className="mt-3 list-decimal space-y-2 pl-5">
            {WEBSITE_DOMAIN_INSTRUCTION_KEYS.map((key) => (
              <Text
                as="li"
                size="small"
                color="secondary"
                className="list-item"
                key={key}
                disableAutoLink
              >
                {t(key)}
              </Text>
            ))}
          </ol>
          <Text size="small" color="secondary" className="mt-3" disableAutoLink>
            {t('websites.settings.domains.instructions.tip')}
          </Text>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="flex-1">
            <LabelTextInput
              label={t('websites.settings.domains.newDomain.label')}
              labelPosition="top"
              inputProps={{
                value: customDomain,
                disabled: disabled || !canAddDomain,
                error: domainError ?? undefined,
                onBlur: () => setShowDomainError(Boolean(customDomain.trim())),
                onChange: (event) => onCustomDomainChange(event.target.value),
                placeholder: t(
                  'websites.settings.domains.newDomain.placeholder'
                ),
              }}
            />
          </div>
          <div className="md:pt-8">
            <Button
              onClick={addDomain}
              disabled={
                disabled ||
                !canAddDomain ||
                !customDomain.trim() ||
                creatingDomain
              }
              loading={creatingDomain}
            >
              {t('websites.settings.domains.actions.add')}
            </Button>
          </div>
        </div>
        {canAddDomain ? null : (
          <Text size="small" color="secondary">
            {t('websites.settings.domains.publishFirst')}
          </Text>
        )}
        <div className="space-y-3">
          {(website?.domains ?? []).map((domain) => (
            <WebsiteDomainCard
              key={domain.id}
              domain={domain}
              disabled={disabled}
              verifying={verifyingDomain}
              onVerify={onVerifyDomain}
            />
          ))}
        </div>
      </InlineFormSection>

      <InlineFormSection
        title={t('websites.settings.status.title')}
        icon={<PiInfoLight className="h-5 w-5" aria-hidden />}
      >
        <WebsiteStatusSummary
          website={website}
          liveSiteUrl={liveSiteUrl}
          provisionalUrl={statusProvisionalUrl}
        />
      </InlineFormSection>
    </section>
  );
};
